/** Comercial acotado movil: mismos endpoints que la web (design.md §22.5).
 *
 * Los importes del backend (Decimal) pueden llegar como `number` o `string`:
 * se normalizan con `Number()` al mapear para que las tarjetas formateen
 * sin distinguir el origen.
 */
import { mobileApiClient, newIdempotencyKey } from './mobileApiClient';

export type ListResponse<T> = { items: T[]; total: number };

/** Decimal del backend: numero o cadena serializada. */
export type DecimalLike = number | string;

export type MobileClientDto = {
  id: string;
  legal_name: string;
  tax_id?: string | null;
  business_name?: string | null;
  code?: string;
};

export type RequirementClientBrief = {
  id?: string;
  code?: string;
  legal_name?: string;
  tax_id?: string | null;
};

export type MobileRequirementDto = {
  id: string;
  code: string;
  name: string;
  description?: string | null;
  client_id: string;
  client?: RequirementClientBrief | null;
  commercial_owner_id?: string | null;
  requested_date?: string;
  required_date: string;
  type: string;
  status: string;
  source?: string | null;
  /** Revisión del documento para el header X-Expected-Document-Revision. */
  workflow_revision: number;
  created_by?: string | null;
  /** Alias de compatibilidad: equivale a `name`. */
  title?: string;
};

export type BudgetRequirementBrief = {
  id: string;
  code?: string;
  name?: string;
  client_id?: string;
  client?: RequirementClientBrief | null;
};

export type MobileBudgetDto = {
  id: string;
  budget_code: string;
  version_number: number;
  status: string;
  budget_date?: string;
  currency?: string;
  exchange_rate?: DecimalLike;
  subtotal?: DecimalLike;
  adjustment_amount?: DecimalLike;
  total_cost?: DecimalLike;
  sale_price?: DecimalLike;
  estimated_margin?: DecimalLike;
  customer_requirement_id?: string;
  customer_requirement?: BudgetRequirementBrief | null;
  created_by?: string | null;
  /** Alias de compatibilidad: equivale a `budget_code`. */
  code?: string;
  /** Total normalizado a numero para las tarjetas (equivale a `sale_price`). */
  total?: number;
};

export type QuotationProjectBrief = {
  id?: string;
  code?: string;
  name?: string;
  client_id?: string;
  client_legal_name?: string | null;
  client_tax_id?: string | null;
};

export type QuotationBudgetBrief = {
  id?: string;
  budget_code?: string;
  version_number?: number;
  currency?: string;
  total_cost?: DecimalLike;
  sale_price?: DecimalLike;
};

export type MobileQuotationDto = {
  id: string;
  quotation_number: string;
  status: string;
  issue_date?: string;
  valid_until?: string;
  currency?: string;
  subtotal?: DecimalLike;
  discount?: DecimalLike;
  tax?: DecimalLike;
  total?: DecimalLike | number;
  created_by?: string | null;
  project?: QuotationProjectBrief | null;
  budget?: QuotationBudgetBrief | null;
  /** Alias de compatibilidad: equivale a `quotation_number`. */
  code?: string;
};

export type CatalogItemDto = {
  id: string;
  code: string;
  name: string;
  item_type?: string;
  unit_code?: string | null;
  currency_code?: string;
  unit_price?: DecimalLike;
  has_composition?: boolean;
  /** Precio normalizado a numero para el cotizador (equivale a `unit_price`). */
  price?: number;
};

/** Nodo de composicion de un item de catalogo (solo lectura, sin importes). */
export type CompositionNode = {
  master_item_id?: string;
  code?: string;
  name?: string;
  quantity?: DecimalLike;
  children?: CompositionNode[];
};

export type MobileAttachmentDto = {
  id: string;
  file_name: string;
  file_size?: number;
  content_type?: string;
  description?: string | null;
  created_at?: string;
};

export type MobileObservationDto = {
  id: string;
  customer_requirement_id: string;
  comment: string;
  status: string;
  resolved_at?: string | null;
  resolution_comment?: string | null;
  created_at?: string;
  updated_at?: string;
};

export type MobileBudgetSnapshotDto = {
  id: string;
  budget_id: string;
  snapshot_number: number;
  comments?: string | null;
  created_at?: string;
};

/** Partida del presupuesto (el servidor enmascara costos al vendedor ciego). */
export type MobileBudgetItemDto = {
  id: string;
  budget_id: string;
  description?: string | null;
  item_type: string;
  line_kind: string;
  quantity: DecimalLike;
  unit?: string | null;
  unit_cost?: DecimalLike;
  base_price?: DecimalLike;
  margin?: DecimalLike | null;
  unit_sale_price?: DecimalLike | null;
  adjustment_type?: string | null;
  adjustment_amount?: DecimalLike;
  adjustment_justification?: string | null;
  master_item_id?: string | null;
  master_item?: { code?: string; name?: string } | null;
  total_cost?: DecimalLike;
};

function normalizeBudgetItem(raw: Record<string, unknown>): MobileBudgetItemDto {
  const master = raw['master_item'] as { code?: string; name?: string } | null | undefined;
  return {
    id: String(raw['id'] ?? ''),
    budget_id: String(raw['budget_id'] ?? ''),
    description: (raw['description'] as string | null) ?? null,
    item_type: typeof raw['item_type'] === 'string' ? (raw['item_type'] as string) : '',
    line_kind: typeof raw['line_kind'] === 'string' ? (raw['line_kind'] as string) : 'technical',
    quantity: (raw['quantity'] as DecimalLike) ?? 1,
    unit: (raw['unit'] as string | null) ?? null,
    unit_cost: raw['unit_cost'] as DecimalLike | undefined,
    base_price: raw['base_price'] as DecimalLike | undefined,
    margin: (raw['margin'] as DecimalLike | null) ?? null,
    unit_sale_price: raw['unit_sale_price'] as DecimalLike | undefined,
    adjustment_type: (raw['adjustment_type'] as string | null) ?? null,
    adjustment_amount: raw['adjustment_amount'] as DecimalLike | undefined,
    adjustment_justification: (raw['adjustment_justification'] as string | null) ?? null,
    master_item_id:
      raw['master_item_id'] === null || raw['master_item_id'] === undefined
        ? null
        : String(raw['master_item_id']),
    master_item: master ?? null,
    total_cost: raw['total_cost'] as DecimalLike | undefined,
  };
}

function normalizeBudgetSnapshot(raw: Record<string, unknown>): MobileBudgetSnapshotDto {
  return {
    id: String(raw['id'] ?? ''),
    budget_id: String(raw['budget_id'] ?? ''),
    snapshot_number: typeof raw['snapshot_number'] === 'number' ? (raw['snapshot_number'] as number) : 0,
    comments: (raw['comments'] as string | null) ?? null,
    created_at: raw['created_at'] === undefined ? undefined : String(raw['created_at'] ?? ''),
  };
}

function toNumber(value: unknown): number | undefined {
  if (value === null || value === undefined) return undefined;
  if (typeof value === 'number') return Number.isFinite(value) ? value : undefined;
  if (typeof value === 'string' && value.trim() !== '') {
    const parsed = Number(value);
    return Number.isFinite(parsed) ? parsed : undefined;
  }
  return undefined;
}

export function normalizeRequirement(raw: Record<string, unknown>): MobileRequirementDto {
  const name = typeof raw['name'] === 'string' ? (raw['name'] as string) : '';
  const fallbackTitle = typeof raw['title'] === 'string' ? (raw['title'] as string) : name;
  return {
    id: String(raw['id'] ?? ''),
    code: typeof raw['code'] === 'string' ? (raw['code'] as string) : '',
    name: name || fallbackTitle,
    description: (raw['description'] as string | null) ?? null,
    client_id: String(raw['client_id'] ?? ''),
    client: (raw['client'] as RequirementClientBrief | null) ?? null,
    commercial_owner_id:
      raw['commercial_owner_id'] === null || raw['commercial_owner_id'] === undefined
        ? null
        : String(raw['commercial_owner_id']),
    requested_date:
      raw['requested_date'] === undefined ? undefined : String(raw['requested_date'] ?? ''),
    required_date: String(raw['required_date'] ?? ''),
    type: typeof raw['type'] === 'string' ? (raw['type'] as string) : 'standard',
    status: typeof raw['status'] === 'string' ? (raw['status'] as string) : '',
    source: (raw['source'] as string | null) ?? null,
    workflow_revision:
      typeof raw['workflow_revision'] === 'number'
        ? (raw['workflow_revision'] as number)
        : Number(raw['workflow_revision'] ?? 0) || 0,
    created_by:
      raw['created_by'] === null || raw['created_by'] === undefined
        ? null
        : String(raw['created_by']),
    title: name || fallbackTitle,
  };
}

function normalizeBudget(raw: Record<string, unknown>): MobileBudgetDto {
  const salePrice = toNumber(raw['sale_price'] ?? raw['total']);
  return {
    id: String(raw['id'] ?? ''),
    budget_code: typeof raw['budget_code'] === 'string' ? (raw['budget_code'] as string) : '',
    version_number: typeof raw['version_number'] === 'number' ? (raw['version_number'] as number) : 1,
    status: typeof raw['status'] === 'string' ? (raw['status'] as string) : '',
    budget_date: raw['budget_date'] === undefined ? undefined : String(raw['budget_date'] ?? ''),
    currency: typeof raw['currency'] === 'string' ? (raw['currency'] as string) : undefined,
    exchange_rate: raw['exchange_rate'] as DecimalLike | undefined,
    subtotal: (raw['subtotal'] as DecimalLike | undefined),
    adjustment_amount: (raw['adjustment_amount'] as DecimalLike | undefined),
    total_cost: (raw['total_cost'] as DecimalLike | undefined),
    sale_price: (raw['sale_price'] as DecimalLike | undefined),
    estimated_margin: (raw['estimated_margin'] as DecimalLike | undefined),
    customer_requirement_id:
      raw['customer_requirement_id'] === undefined
        ? undefined
        : String(raw['customer_requirement_id'] ?? ''),
    customer_requirement: (raw['customer_requirement'] as BudgetRequirementBrief | null) ?? null,
    created_by:
      raw['created_by'] === null || raw['created_by'] === undefined
        ? null
        : String(raw['created_by']),
    code: typeof raw['budget_code'] === 'string' ? (raw['budget_code'] as string) : undefined,
    total: salePrice,
  };
}

function normalizeQuotation(raw: Record<string, unknown>): MobileQuotationDto {
  const quotationNumber =
    typeof raw['quotation_number'] === 'string'
      ? (raw['quotation_number'] as string)
      : typeof raw['code'] === 'string'
        ? (raw['code'] as string)
        : '';
  return {
    id: String(raw['id'] ?? ''),
    quotation_number: quotationNumber,
    status: typeof raw['status'] === 'string' ? (raw['status'] as string) : '',
    issue_date: raw['issue_date'] === undefined ? undefined : String(raw['issue_date'] ?? ''),
    valid_until: raw['valid_until'] === undefined ? undefined : String(raw['valid_until'] ?? ''),
    currency: typeof raw['currency'] === 'string' ? (raw['currency'] as string) : undefined,
    subtotal: (raw['subtotal'] as DecimalLike | undefined),
    discount: (raw['discount'] as DecimalLike | undefined),
    tax: (raw['tax'] as DecimalLike | undefined),
    total: (raw['total'] as DecimalLike | number | undefined),
    created_by:
      raw['created_by'] === null || raw['created_by'] === undefined
        ? null
        : String(raw['created_by']),
    project: (raw['project'] as QuotationProjectBrief | null) ?? null,
    budget: (raw['budget'] as QuotationBudgetBrief | null) ?? null,
    code: quotationNumber || undefined,
  };
}

function normalizeCatalogItem(raw: Record<string, unknown>): CatalogItemDto {
  const unitPrice = toNumber(raw['unit_price'] ?? raw['price']);
  return {
    id: String(raw['id'] ?? ''),
    code: typeof raw['code'] === 'string' ? (raw['code'] as string) : '',
    name: typeof raw['name'] === 'string' ? (raw['name'] as string) : '',
    item_type: typeof raw['item_type'] === 'string' ? (raw['item_type'] as string) : undefined,
    unit_code: (raw['unit_code'] as string | null) ?? null,
    currency_code:
      typeof raw['currency_code'] === 'string' ? (raw['currency_code'] as string) : undefined,
    unit_price: (raw['unit_price'] as DecimalLike | undefined),
    has_composition: typeof raw['has_composition'] === 'boolean' ? (raw['has_composition'] as boolean) : undefined,
    price: unitPrice,
  };
}

async function list<T>(path: string, params?: Record<string, unknown>): Promise<ListResponse<T>> {
  const { data } = await mobileApiClient.get(path, { params });
  if (Array.isArray(data)) return { items: data as T[], total: (data as T[]).length };
  return data as ListResponse<T>;
}

export const commercialService = {
  // Clientes (vendedor crea desde campo).
  clients(params?: Record<string, unknown>): Promise<ListResponse<MobileClientDto>> {
    return list<MobileClientDto>('/api/v1/clients', params);
  },
  async createClient(body: Record<string, unknown>) {
    const { data } = await mobileApiClient.post('/api/v1/clients', body, {
      headers: { 'Idempotency-Key': newIdempotencyKey() },
    });
    return data;
  },

  // Requerimientos.
  async requirements(
    params?: Record<string, unknown>,
  ): Promise<ListResponse<MobileRequirementDto>> {
    const response = await list<Record<string, unknown>>('/api/v1/customer-requirements', params);
    return { items: response.items.map(normalizeRequirement), total: response.total };
  },
  async getRequirement(id: string): Promise<MobileRequirementDto> {
    const { data } = await mobileApiClient.get(`/api/v1/customer-requirements/${id}`);
    return normalizeRequirement(data as Record<string, unknown>);
  },
  async createRequirement(body: Record<string, unknown>) {
    const { data } = await mobileApiClient.post('/api/v1/customer-requirements', body, {
      headers: { 'Idempotency-Key': newIdempotencyKey() },
    });
    return data;
  },
  async requirementAction(
    id: string,
    action: string,
    body: Record<string, unknown> = {},
    revision?: number,
  ) {
    const { data } = await mobileApiClient.post(
      `/api/v1/customer-requirements/${id}/${action}`,
      body,
      {
        headers: {
          'Idempotency-Key': newIdempotencyKey(),
          ...(revision === undefined ? {} : { 'X-Expected-Document-Revision': String(revision) }),
        },
      },
    );
    return data;
  },
  /** Actualiza un requerimiento (PUT, el estado viaja solo por acciones de flujo). */
  async updateRequirement(id: string, body: Record<string, unknown>, revision?: number) {
    const { data } = await mobileApiClient.put(
      `/api/v1/customer-requirements/${id}`,
      body,
      {
        headers: {
          'Idempotency-Key': newIdempotencyKey(),
          ...(revision === undefined ? {} : { 'X-Expected-Document-Revision': String(revision) }),
        },
      },
    );
    return data;
  },

  // Observaciones de requerimiento (sub-items REST; resolver queda en la web).
  async listObservations(id: string): Promise<MobileObservationDto[]> {
    const { data } = await mobileApiClient.get(`/api/v1/customer-requirements/${id}/observations`);
    if (Array.isArray(data)) return data as MobileObservationDto[];
    const items = (data as { items?: MobileObservationDto[] }).items;
    return items ?? [];
  },
  async createObservation(id: string, comment: string) {
    const { data } = await mobileApiClient.post(
      `/api/v1/customer-requirements/${id}/observations`,
      { comment },
      { headers: { 'Idempotency-Key': newIdempotencyKey() } },
    );
    return data;
  },
  async updateObservation(requirementId: string, observationId: string, comment: string) {
    const { data } = await mobileApiClient.put(
      `/api/v1/customer-requirements/${requirementId}/observations/${observationId}`,
      { comment },
      { headers: { 'Idempotency-Key': newIdempotencyKey() } },
    );
    return data;
  },
  async deleteObservation(requirementId: string, observationId: string) {
    const { data } = await mobileApiClient.delete(
      `/api/v1/customer-requirements/${requirementId}/observations/${observationId}`,
    );
    return data;
  },
  /** Lista los adjuntos de un requerimiento (solo lectura, tras el alta). */
  async requirementAttachments(id: string): Promise<MobileAttachmentDto[]> {
    const { data } = await mobileApiClient.get(`/api/v1/customer-requirements/${id}/attachments`);
    if (Array.isArray(data)) return data as MobileAttachmentDto[];
    const items = (data as { items?: MobileAttachmentDto[] }).items;
    return items ?? [];
  },
  /** Lista los adjuntos de un presupuesto (ruta real GET /api/v1/budgets/{id}/attachments). */
  async budgetAttachments(id: string): Promise<MobileAttachmentDto[]> {
    const { data } = await mobileApiClient.get(`/api/v1/budgets/${id}/attachments`);
    if (Array.isArray(data)) return data as MobileAttachmentDto[];
    const items = (data as { items?: MobileAttachmentDto[] }).items;
    return items ?? [];
  },

  // Cotizador: compone requerimientos nuevos (contrato vigente design.md §22.5).
  // El POST borrado `/api/v1/commercial/quotations` ya no existe (404/405).
  async simulate(body: Record<string, unknown>) {
    const { data } = await mobileApiClient.post('/api/v1/commercial/simulations', body);
    return data;
  },
  async composeRequirement(body: Record<string, unknown>): Promise<MobileRequirementDto> {
    const { data } = await mobileApiClient.post(
      '/api/v1/commercial/requirement-compositions',
      body,
      { headers: { 'Idempotency-Key': newIdempotencyKey() } },
    );
    return normalizeRequirement(data as Record<string, unknown>);
  },

  // Lectura del documento de cotizacion para el detalle de tareas (sin lista ni acciones).
  async getQuotation(id: string): Promise<MobileQuotationDto> {
    const { data } = await mobileApiClient.get(`/api/v1/quotations/${id}`);
    return normalizeQuotation(data as Record<string, unknown>);
  },
  async budgets(params?: Record<string, unknown>): Promise<ListResponse<MobileBudgetDto>> {
    const response = await list<Record<string, unknown>>('/api/v1/budgets', params);
    return { items: response.items.map(normalizeBudget), total: response.total };
  },
  async getBudget(id: string): Promise<MobileBudgetDto> {
    const { data } = await mobileApiClient.get(`/api/v1/budgets/${id}`);
    return normalizeBudget(data as Record<string, unknown>);
  },

  /** Acción de flujo del presupuesto (send-to-commercial, request-workflow, cancel). */
  async budgetAction(budgetId: string, action: string, body: Record<string, unknown> = {}, revision?: number) {
    const { data } = await mobileApiClient.post(
      `/api/v1/budgets/${budgetId}/${action}`,
      body,
      {
        headers: {
          'Idempotency-Key': newIdempotencyKey(),
          ...(revision === undefined ? {} : { 'X-Expected-Document-Revision': String(revision) }),
        },
      },
    );
    return data;
  },

  // Partidas del presupuesto (lectura + edición comercial de vendedor).
  async listBudgetItems(budgetId: string): Promise<MobileBudgetItemDto[]> {
    const { data } = await mobileApiClient.get(`/api/v1/budgets/${budgetId}/items`);
    const rawItems = (Array.isArray(data) ? data : (data as { items?: unknown[] }).items ?? []) as Record<
      string,
      unknown
    >[];
    return rawItems.map(normalizeBudgetItem);
  },
  async addBudgetItem(budgetId: string, body: Record<string, unknown>, revision?: number) {
    const { data } = await mobileApiClient.post(`/api/v1/budgets/${budgetId}/items`, body, {
      headers: {
        'Idempotency-Key': newIdempotencyKey(),
        ...(revision === undefined ? {} : { 'X-Expected-Document-Revision': String(revision) }),
      },
    });
    return data;
  },
  async updateBudgetItem(itemId: string, body: Record<string, unknown>, revision?: number) {
    const { data } = await mobileApiClient.put(`/api/v1/budget-items/${itemId}`, body, {
      headers: {
        'Idempotency-Key': newIdempotencyKey(),
        ...(revision === undefined ? {} : { 'X-Expected-Document-Revision': String(revision) }),
      },
    });
    return data;
  },
  async deleteBudgetItem(itemId: string, revision?: number) {
    const { data } = await mobileApiClient.delete(`/api/v1/budget-items/${itemId}`, {
      headers: {
        ...(revision === undefined ? {} : { 'X-Expected-Document-Revision': String(revision) }),
      },
    });
    return data;
  },
  /** Aplica un ajuste global (descuento/recargo con justificación, lado comercial). */
  async applyBudgetAdjustment(budgetId: string, body: Record<string, unknown>, revision?: number) {
    const { data } = await mobileApiClient.post(`/api/v1/budgets/${budgetId}/adjustments`, body, {
      headers: {
        'Idempotency-Key': newIdempotencyKey(),
        ...(revision === undefined ? {} : { 'X-Expected-Document-Revision': String(revision) }),
      },
    });
    return data;
  },
  async clearBudgetAdjustment(itemId: string, revision?: number) {
    const { data } = await mobileApiClient.delete(`/api/v1/budget-items/${itemId}/adjustment`, {
      headers: {
        ...(revision === undefined ? {} : { 'X-Expected-Document-Revision': String(revision) }),
      },
    });
    return data;
  },
  async simulateBudget(budgetId: string, body: Record<string, unknown> = {}) {
    const { data } = await mobileApiClient.post(`/api/v1/budgets/${budgetId}/simulate`, body);
    return data;
  },
  async calculateBudget(budgetId: string) {
    const { data } = await mobileApiClient.post(`/api/v1/budgets/${budgetId}/calculate`);
    return data;
  },
  /** Genera la cotización desde un presupuesto aprobado (no navega, devuelve la lectura). */
  async generateQuotation(budgetId: string): Promise<MobileQuotationDto> {
    const { data } = await mobileApiClient.post(
      `/api/v1/budgets/${budgetId}/quotations`,
      {},
      { headers: { 'Idempotency-Key': newIdempotencyKey() } },
    );
    return normalizeQuotation(data as Record<string, unknown>);
  },
  /** Linaje de versiones (snapshots) de un presupuesto, más reciente primero. */
  async listBudgetSnapshots(budgetId: string): Promise<MobileBudgetSnapshotDto[]> {
    const { data } = await mobileApiClient.get(`/api/v1/budgets/${budgetId}/snapshots`, {
      params: { page_size: 100 },
    });
    const rawItems = (Array.isArray(data) ? data : (data as { items?: unknown[] }).items ?? []) as Record<
      string,
      unknown
    >[];
    return rawItems.map(normalizeBudgetSnapshot);
  },

  async exportQuotationPdf(id: string): Promise<Blob> {
    const response = await mobileApiClient.get(`/api/v1/quotations/${id}/pdf`, {
      responseType: 'blob',
    });
    return response.data as Blob;
  },

  // Catalogo del cotizador (venta de catalogo por grupo).
  async catalogItems(params?: Record<string, unknown>): Promise<CatalogItemDto[]> {
    const query: Record<string, unknown> = {};
    if (params) {
      const pageSize = params['page_size'] ?? params['limit'];
      if (pageSize !== undefined) query['limit'] = pageSize;
      if (params['limit'] !== undefined) query['limit'] = params['limit'];
      if (params['skip'] !== undefined) query['skip'] = params['skip'];
      if (params['search'] !== undefined) query['search'] = params['search'];
      if (params['group'] !== undefined) query['group'] = params['group'];
    }
    if (query['limit'] === undefined) query['limit'] = 50;
    const { data } = await mobileApiClient.get('/api/v1/commercial/catalog-items', {
      params: query,
    });
    const rawItems = (data.items ?? data) as Record<string, unknown>[];
    return (Array.isArray(rawItems) ? rawItems : []).map(normalizeCatalogItem);
  },
  /** Composicion de un item de catalogo (hijos sin importes). */
  async catalogComposition(itemId: string, quantity?: number): Promise<CompositionNode> {
    const { data } = await mobileApiClient.get(
      `/api/v1/commercial/catalog-items/${itemId}/composition`,
      { params: quantity === undefined ? {} : { quantity } },
    );
    return data as CompositionNode;
  },

  // Adjuntos desde camara/galeria/archivos (multipart, max 10 MB en servidor).
  async uploadAttachments(
    entityPath: 'budgets' | 'quotations' | 'customer-requirements' | 'master-items',
    entityId: string,
    files: File[],
    description = '',
  ) {
    const form = new FormData();
    for (const file of files) form.append('files', file);
    form.append('description', description);
    const { data } = await mobileApiClient.post(
      `/api/v1/${entityPath}/${entityId}/attachments/batch`,
      form,
      { headers: { 'Content-Type': 'multipart/form-data' } },
    );
    return data;
  },
};
